import moment from 'moment';
import type { JobCode, SchedulerDepartment } from 'core/API/scheduler';
import {
	filterJobCodesByDepartments,
	getJobCodeDepartmentId,
	sortDepartmentsAlphabetically,
} from './scheduleDepartmentUtils';
import {
	isEmployeeEligibleForDepartment,
	isEmployeeScheduleable,
} from './jobCodeEligibility';

export type DepartmentFilterSelection = number[] | 'all';

export const getDefaultDepartmentFilter = (
	departments: SchedulerDepartment[]
): DepartmentFilterSelection =>
	departments.length === 0 ? 'all' : departments.map((d) => d.id);

export const isAllDepartmentsSelected = (
	selectedDepartmentIds: DepartmentFilterSelection,
	departments: SchedulerDepartment[]
): boolean =>
	selectedDepartmentIds === 'all' ||
	(departments.length > 0 &&
		selectedDepartmentIds.length === departments.length &&
		departments.every((d) => selectedDepartmentIds.includes(d.id)));

export interface ScheduleResourceRow {
	id: string | number;
	name: string;
	displayName?: string;
	departmentId?: number | null;
	departmentName?: string;
	isDepartmentHeader?: boolean;
	isOpenShift?: boolean;
	isHouseShiftRow?: boolean;
	jobCode?: JobCode;
	employee?: any;
	background_color?: string;
	text_color?: string;
	sortOrder?: number;
	totalHours?: number;
	[key: string]: unknown;
}

export const isDepartmentHeaderResourceId = (resourceId: string | number | undefined): boolean =>
	String(resourceId ?? '').startsWith('dept-header-');

export const isOpenShiftResourceId = (resourceId: string | number | undefined): boolean => {
	const id = String(resourceId ?? '');
	return id === 'open-shifts' || id.startsWith('house-');
};

export const parseWeekViewEmployeeId = (
	resourceId: string | number | undefined
): string | null => {
	const id = String(resourceId ?? '');
	if (!id || isDepartmentHeaderResourceId(id) || isOpenShiftResourceId(id)) {
		return null;
	}

	const departmentSuffix = id.match(/^(.*)-(\d+|none)$/);
	if (departmentSuffix) {
		return departmentSuffix[1];
	}

	return id;
};

export const hasDepartmentGroupedResources = (
	resources: Array<{ isDepartmentHeader?: boolean; id?: string | number }> | undefined
): boolean =>
	Boolean(
		resources?.some(
			(resource) =>
				resource.isDepartmentHeader || isDepartmentHeaderResourceId(resource.id)
		)
	);

export const findEmployeeResourceIds = (
	resources: Array<{
		id?: string | number;
		isDepartmentHeader?: boolean;
		isHouseShiftRow?: boolean;
		isOpenShift?: boolean;
		employee?: { id?: string | number };
	}> | undefined,
	employeeId: string | number | undefined
): Array<string | number> => {
	if (employeeId == null || !resources?.length) return [];

	const employeeKey = String(employeeId);
	return resources
		.filter((resource) => {
			if (
				resource.isDepartmentHeader ||
				resource.isHouseShiftRow ||
				resource.isOpenShift ||
				isDepartmentHeaderResourceId(resource.id)
			) {
				return false;
			}

			const resourceId = String(resource.id ?? '');
			if (resourceId === employeeKey || resourceId.startsWith(`${employeeKey}-`)) {
				return true;
			}

			return String(resource.employee?.id ?? '') === employeeKey;
		})
		.map((resource) => resource.id as string | number);
};

const compareByLabel = (a: JobCode, b: JobCode) => a.label.localeCompare(b.label);

const compareEmployeesByLastName = (
	a: { lastName?: string; firstName?: string },
	b: { lastName?: string; firstName?: string }
) => {
	const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
	if (lastNameCompare !== 0) return lastNameCompare;
	return (a.firstName || '').localeCompare(b.firstName || '');
};

const getEmployeeKey = (employee: { id?: string | number } | null | undefined): string | null => {
	if (employee?.id == null || employee.id === '') return null;
	return String(employee.id);
};

const buildEmployeeLookup = (employees: any[] | undefined): Map<string, any> => {
	const lookup = new Map<string, any>();
	(employees || []).forEach((employee) => {
		const key = getEmployeeKey(employee);
		if (!key) return;
		lookup.set(key, employee);
	});
	return lookup;
};

const mergeWeekViewEmployee = (employee: any, lookup: Map<string, any>): any => {
	const key = getEmployeeKey(employee);
	const fromRoster = key ? lookup.get(key) : undefined;
	if (!employee && !fromRoster) return employee;
	if (!fromRoster) return employee;
	if (!employee) return fromRoster;

	return {
		...employee,
		...fromRoster,
		firstName: fromRoster.firstName || employee.firstName || '',
		lastName: fromRoster.lastName || employee.lastName || '',
		userAvailability: fromRoster.userAvailability || employee.userAvailability || [],
		eligibleJobCodes: fromRoster.eligibleJobCodes || employee.eligibleJobCodes || [],
	};
};

const toWeekViewEmployeeRow = (employee: any, totalHours: number, extra: Partial<ScheduleResourceRow> = {}): ScheduleResourceRow => {
	const firstName = employee?.firstName || '';
	const lastName = employee?.lastName || '';
	const displayName = `${firstName} ${lastName}`.trim() || 'Employee';

	return {
		id: employee.id,
		name: `${displayName} (${totalHours.toFixed(1)}h)`,
		displayName,
		employee,
		userAvailability: employee?.userAvailability || [],
		totalHours,
		...extra,
	};
};

export const TIME_OFF_RESOURCE_ID = 'timeoff';

export const isTimeOffResourceId = (resourceId: string | number | undefined): boolean =>
	String(resourceId ?? '') === TIME_OFF_RESOURCE_ID;

export const buildTimeOffResource = (sortOrder = -1): ScheduleResourceRow => ({
	id: TIME_OFF_RESOURCE_ID,
	name: 'Time Off',
	displayName: 'Time Off',
	sortOrder,
});

export const buildDayViewResources = ({
	jobCodes,
	departments,
	selectedDepartmentIds,
}: {
	jobCodes: JobCode[];
	departments: SchedulerDepartment[];
	selectedDepartmentIds: DepartmentFilterSelection;
}): ScheduleResourceRow[] => {
	const filteredJobCodes = filterJobCodesByDepartments(jobCodes, selectedDepartmentIds);
	const hasDepartments = departments.length > 0;

	if (!hasDepartments) {
		return [
			buildTimeOffResource(-1),
			...filteredJobCodes
				.sort(compareByLabel)
				.map((jobCode, index) => ({
					id: jobCode.id,
					name: jobCode.jobNumber
						? `${String(jobCode.jobNumber).padStart(2, '0')} ${jobCode.label}`
						: jobCode.label,
					background_color: jobCode.backgroundColor,
					text_color: jobCode.textColor,
					jobCode,
					sortOrder: index,
				})),
		];
	}

	const resources: ScheduleResourceRow[] = [buildTimeOffResource(-1)];
	let sortOrder = 0;
	const sortedDepartments = sortDepartmentsAlphabetically(departments);
	const unassignedJobCodes = filteredJobCodes.filter((jc) => getJobCodeDepartmentId(jc) === null);

	const appendDepartmentGroup = (
		department: SchedulerDepartment | null,
		groupJobCodes: JobCode[]
	) => {
		const departmentId = department?.id ?? null;
		const departmentName = department?.name ?? 'No Department';

		resources.push({
			id: `dept-header-${departmentId ?? 'none'}`,
			name: departmentName,
			displayName: departmentName,
			departmentId,
			departmentName,
			isDepartmentHeader: true,
			sortOrder: sortOrder++,
		});

		[...groupJobCodes].sort(compareByLabel).forEach((jobCode) => {
			resources.push({
				id: jobCode.id,
				name: jobCode.jobNumber
					? `${String(jobCode.jobNumber).padStart(2, '0')} ${jobCode.label}`
					: jobCode.label,
				background_color: jobCode.backgroundColor,
				text_color: jobCode.textColor,
				jobCode,
				departmentId,
				departmentName,
				sortOrder: sortOrder++,
			});
		});
	};

	sortedDepartments.forEach((department) => {
		if (
			selectedDepartmentIds !== 'all' &&
			!selectedDepartmentIds.includes(department.id)
		) {
			return;
		}

		const departmentJobCodes = filteredJobCodes.filter(
			(jc) => getJobCodeDepartmentId(jc) === department.id
		);
		appendDepartmentGroup(department, departmentJobCodes);
	});

	if (unassignedJobCodes.length > 0 && selectedDepartmentIds === 'all') {
		appendDepartmentGroup(null, unassignedJobCodes);
	}

	return resources;
};

export const buildWeekViewResources = ({
	allShifts,
	jobCodes,
	departments,
	selectedDepartmentIds,
	weekStart,
	weekEnd,
	employees = [],
}: {
	allShifts: any[];
	jobCodes: JobCode[];
	departments: SchedulerDepartment[];
	selectedDepartmentIds: DepartmentFilterSelection;
	weekStart: moment.Moment;
	weekEnd: moment.Moment;
	employees?: any[];
}): ScheduleResourceRow[] => {
	const filteredJobCodeIds = new Set(
		filterJobCodesByDepartments(jobCodes, selectedDepartmentIds).map((jc) => jc.id)
	);
	const employeeLookup = buildEmployeeLookup(employees);

	const shiftsInWeek = allShifts.filter((shift) => {
		const shiftStart = moment(shift.start || shift.startAt);
		if (!shiftStart.isBetween(weekStart, weekEnd, null, '[]')) return false;
		if (!shift.jobCode?.id) return false;
		return filteredJobCodeIds.has(shift.jobCode.id);
	});

	if (departments.length === 0) {
		return buildLegacyWeekResources(shiftsInWeek, weekStart, weekEnd, employeeLookup);
	}

	const resources: ScheduleResourceRow[] = [];
	let sortOrder = 0;
	const sortedDepartments = sortDepartmentsAlphabetically(departments);

	const getShiftDepartmentId = (shift: any): number | null => {
		const jobCode = jobCodes.find((jc) => jc.id === shift.jobCode?.id);
		return getJobCodeDepartmentId(jobCode || shift.jobCode);
	};

	const rosterEmployeeKeys = Array.from(employeeLookup.keys());

	const appendDepartmentWeekGroup = (
		department: SchedulerDepartment | null,
		departmentShifts: any[],
		includeRosterEmployees: boolean
	) => {
		const departmentId = department?.id ?? null;
		const departmentName = department?.name ?? 'No Department';
		const houseShifts = departmentShifts.filter((s) => !s.employee?.id);
		const assignedShifts = departmentShifts.filter((s) => s.employee?.id);

		const hoursByEmployeeId = new Map<string, number>();
		const shiftEmployeeById = new Map<string, any>();
		assignedShifts.forEach((shift) => {
			const key = getEmployeeKey(shift.employee);
			if (!key) return;
			const shiftDuration = moment
				.duration(
					moment(shift.end || shift.endAt).diff(moment(shift.start || shift.startAt))
				)
				.asHours();
			hoursByEmployeeId.set(key, (hoursByEmployeeId.get(key) || 0) + shiftDuration);
			if (!shiftEmployeeById.has(key)) {
				shiftEmployeeById.set(key, shift.employee);
			}
		});

		const employeeKeys = new Set<string>([
			...(includeRosterEmployees
				? rosterEmployeeKeys.filter((key) => {
						const employee = employeeLookup.get(key);
						return (
							!!employee &&
							isEmployeeEligibleForDepartment(employee, departmentId, jobCodes)
						);
					})
				: []),
			...hoursByEmployeeId.keys(),
		]);

		if (houseShifts.length === 0 && employeeKeys.size === 0) {
			return;
		}

		resources.push({
			id: `dept-header-${departmentId ?? 'none'}`,
			name: departmentName,
			displayName: departmentName,
			departmentId,
			departmentName,
			isDepartmentHeader: true,
			sortOrder: sortOrder++,
		});

		if (houseShifts.length > 0) {
			const totalHouseHours = houseShifts.reduce((sum, shift) => {
				return sum + moment.duration(moment(shift.end).diff(moment(shift.start))).asHours();
			}, 0);

			resources.push({
				id: `house-${departmentId ?? 'none'}`,
				name: `House Shifts (${totalHouseHours.toFixed(1)}h)`,
				displayName: 'House Shifts',
				departmentId,
				departmentName,
				isOpenShift: true,
				isHouseShiftRow: true,
				totalHours: totalHouseHours,
				sortOrder: sortOrder++,
			});
		}

		Array.from(employeeKeys)
			.map((key) => {
				const employee = mergeWeekViewEmployee(
					employeeLookup.get(key) || shiftEmployeeById.get(key),
					employeeLookup
				);
				return {
					employee,
					totalHours: hoursByEmployeeId.get(key) || 0,
				};
			})
			.filter((row) => row.employee?.id != null)
			.sort((a, b) => compareEmployeesByLastName(a.employee, b.employee))
			.forEach(({ employee, totalHours }) => {
				resources.push(
					toWeekViewEmployeeRow(employee, totalHours, {
						id: `${employee.id}-${departmentId ?? 'none'}`,
						departmentId,
						departmentName,
						sortOrder: sortOrder++,
					})
				);
			});
	};

	const shiftsByDepartment = new Map<number | 'none', any[]>();
	shiftsInWeek.forEach((shift) => {
		const deptId = getShiftDepartmentId(shift);
		const key = deptId ?? 'none';
		if (!shiftsByDepartment.has(key)) shiftsByDepartment.set(key, []);
		shiftsByDepartment.get(key)!.push(shift);
	});

	sortedDepartments.forEach((department) => {
		if (
			selectedDepartmentIds !== 'all' &&
			!selectedDepartmentIds.includes(department.id)
		) {
			return;
		}
		appendDepartmentWeekGroup(department, shiftsByDepartment.get(department.id) || [], true);
	});

	if (selectedDepartmentIds === 'all') {
		const unassignedShifts = shiftsByDepartment.get('none') || [];
		if (unassignedShifts.length > 0) {
			appendDepartmentWeekGroup(null, unassignedShifts, false);
		}
	}

	return resources;
};

const buildLegacyWeekResources = (
	shiftsInWeek: any[],
	weekStart: moment.Moment,
	weekEnd: moment.Moment,
	employeeLookup: Map<string, any>
): ScheduleResourceRow[] => {
	const employeeResourcesMap = new Map<string, any>();

	employeeLookup.forEach((employee, key) => {
		if (!isEmployeeScheduleable(employee)) return;
		employeeResourcesMap.set(key, toWeekViewEmployeeRow(employee, 0));
	});

	shiftsInWeek.forEach((shift) => {
		const shiftStart = moment(shift.start || shift.startAt);
		const shiftEnd = moment(shift.end || shift.endAt);
		if (!shiftStart.isBetween(weekStart, weekEnd, null, '[]')) return;

		const shiftHours = moment.duration(shiftEnd.diff(shiftStart)).asHours();
		const employee = shift.employee;
		const employeeKey = getEmployeeKey(employee);

		if (employeeKey) {
			const mergedEmployee = mergeWeekViewEmployee(employee, employeeLookup);
			if (!employeeResourcesMap.has(employeeKey)) {
				employeeResourcesMap.set(
					employeeKey,
					toWeekViewEmployeeRow(mergedEmployee, shiftHours)
				);
			} else {
				employeeResourcesMap.get(employeeKey).totalHours += shiftHours;
			}
		} else {
			const openShiftKey = 'open-shifts';
			if (!employeeResourcesMap.has(openShiftKey)) {
				employeeResourcesMap.set(openShiftKey, {
					id: openShiftKey,
					name: 'House Shifts',
					displayName: 'House Shifts',
					employee: null,
					totalHours: shiftHours,
					isOpenShift: true,
				});
			} else {
				employeeResourcesMap.get(openShiftKey).totalHours += shiftHours;
			}
		}
	});

	const employeeResources = Array.from(employeeResourcesMap.values()).map((resource) => ({
		...resource,
		name: `${resource.isOpenShift ? 'House Shifts' : resource.displayName} (${resource.totalHours.toFixed(1)}h)`,
		displayName: resource.isOpenShift ? 'House Shifts' : resource.displayName,
	}));

	employeeResources.sort((a, b) => {
		if (a.isOpenShift && !b.isOpenShift) return 1;
		if (!a.isOpenShift && b.isOpenShift) return -1;
		return compareEmployeesByLastName(
			a.employee || { lastName: a.displayName, firstName: '' },
			b.employee || { lastName: b.displayName, firstName: '' }
		);
	});

	return employeeResources;
};

export const resolveShiftResourceId = ({
	shift,
	calendarView,
	jobCodes,
	departments,
}: {
	shift: any;
	calendarView: string;
	jobCodes: JobCode[];
	departments: SchedulerDepartment[];
}): string | number | undefined => {
	const isWeekView = calendarView === 'week' || calendarView === 'workweek';
	const hasDepartments = departments.length > 0;

	if (isWeekView) {
		if (!shift.employee?.id) {
			if (hasDepartments) {
				const jobCode = jobCodes.find((jc) => jc.id === shift.jobCode?.id);
				const deptId = getJobCodeDepartmentId(jobCode || shift.jobCode);
				return `house-${deptId ?? 'none'}`;
			}
			return 'open-shifts';
		}

		if (hasDepartments) {
			const jobCode = jobCodes.find((jc) => jc.id === shift.jobCode?.id);
			const deptId = getJobCodeDepartmentId(jobCode || shift.jobCode);
			return `${shift.employee.id}-${deptId ?? 'none'}`;
		}

		return shift.employee.id;
	}

	return shift.jobCode?.id;
};

export const filterShiftsByDepartments = (
	shifts: any[],
	jobCodes: JobCode[],
	selectedDepartmentIds: DepartmentFilterSelection
): any[] => {
	if (selectedDepartmentIds === 'all') return shifts;

	const allowedJobCodeIds = new Set(
		filterJobCodesByDepartments(jobCodes, selectedDepartmentIds).map((jc) => jc.id)
	);

	return shifts.filter((shift) => shift.jobCode?.id && allowedJobCodeIds.has(shift.jobCode.id));
};
